import type {
    ChangeEvent,
    FC,
    FormEvent,
    ReactNode
} from 'react'
import type { ContactTopic } from '@/constants/contact'

import { useEffect, useRef, useState } from 'react'
import {
    Check,
    ChevronDown,
    CircleAlert,
    LoaderCircle,
    PartyPopper,
    Send
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/generals/button'
import { Confetti } from '@/components/generals/confetti'
import { IconArt } from '@/components/generals/icon-art'
import { Scallop } from '@/components/generals/scallop'
import { contactDetails } from '@/constants/site'

import {
    CONTACT_LIMITS,
    CONTACT_TOPICS,
    EMAIL_PATTERN
} from '@/constants/contact'

export interface TopicRequest {
    topic: ContactTopic
    id: number
}

interface ContactProps {
    topicRequest: TopicRequest | null
}

type Status = 'idle' | 'submitting' | 'success' | 'error'
type FieldName = 'name' | 'email' | 'phone' | 'topic' | 'message'
type Values = Record<FieldName, string>
type Errors = Partial<Record<FieldName, string>>

interface ContactResponse {
    ok?: boolean
    message?: string
    errors?: Errors
}

const INITIAL_VALUES: Values = {
    name: '',
    email: '',
    phone: '',
    topic: '',
    message: ''
}

const PROMISES = [
    "A free 30-minute intro call",
    "A fixed quote within 48 hours",
    "No pressure and no spam, ever"
]

const inputClasses = 'w-full rounded-2xl border-2 border-plum/25 bg-white/70 px-4 py-3 text-base text-plum transition-all duration-200 placeholder:text-plum/40 hover:border-plum/50 focus:-translate-y-0.5 focus:border-plum focus:bg-white focus:shadow-[0_4px_0_0_var(--color-plum)] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-butter/70 aria-[invalid=true]:border-red-600 aria-[invalid=true]:animate-jelly'

const validate = (values: Values): Errors => {
    const errors: Errors = {}
    const email = values.email.trim()

    if (!values.name.trim()) errors.name = "Please tell us your name."

    if (!email) errors.email = "We need an email address to reply to."
    else if (!EMAIL_PATTERN.test(email)) errors.email = "That email address doesn't look quite right."

    if (values.message.trim().length < 10) errors.message = "Tell us a little more, a sentence or two is plenty."

    return errors
}

interface FormFieldProps {
    id: string
    label: string
    optional?: boolean
    error?: string
    className?: string
    children: ReactNode
}

const FormField: FC<FormFieldProps> = ({
    id,
    label,
    optional = false,
    error,
    className,
    children
}): ReactNode => {
    return (
        <div className={className}>
            <label htmlFor={id} className='flex items-baseline justify-between gap-3 font-heading text-lg tracking-wide text-plum'>
                {label}

                {optional && (
                    <span className='font-sans text-sm tracking-normal text-plum/60'>
                        Optional
                    </span>
                )}
            </label>

            <div className='relative mt-2'>
                {children}
            </div>

            {error && (
                <p id={`${id}-error`} className='mt-2 flex items-center gap-1.5 text-sm font-medium text-red-700'>
                    <CircleAlert className='size-4 shrink-0' aria-hidden='true' />
                    {error}
                </p>
            )}
        </div>
    )
}

export const Contact: FC<ContactProps> = ({ topicRequest }): ReactNode => {
    const [values, setValues] = useState<Values>(INITIAL_VALUES)
    const [errors, setErrors] = useState<Errors>({})
    const [status, setStatus] = useState<Status>('idle')
    const [serverMessage, setServerMessage] = useState('')
    const [honeypot, setHoneypot] = useState('')
    const successRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!topicRequest) return

        setValues((current) => ({ ...current, topic: topicRequest.topic }))
        setStatus((current) => (current === 'success' ? 'idle' : current))
    }, [topicRequest])

    useEffect(() => {
        if (status === 'success') successRef.current?.focus()
    }, [status])

    const update = (field: FieldName) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
        const { value } = event.target

        setValues((current) => ({ ...current, [field]: value }))
        if (errors[field]) setErrors((current) => ({ ...current, [field]: undefined }))
    }

    const describedBy = (field: FieldName): string | undefined => {
        return errors[field] ? `contact-${field}-error` : undefined
    }

    const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        const found = validate(values)
        setErrors(found)

        const firstInvalid = (['name', 'email', 'message'] as FieldName[]).find((field) => found[field])
        if (firstInvalid) {
            document.getElementById(`contact-${firstInvalid}`)?.focus()
            return
        }

        setStatus('submitting')
        setServerMessage('')

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...values, website: honeypot })
            })

            const data = (await response.json().catch(() => null)) as ContactResponse | null

            if (!response.ok || !data?.ok) {
                if (data?.errors) setErrors(data.errors)
                setServerMessage(data?.message ?? "Something went wrong on our side. Please try again in a moment.")
                setStatus('error')
                return
            }

            setValues(INITIAL_VALUES)
            setErrors({})
            setStatus('success')
        }
        catch {
            setServerMessage("We couldn't reach our server. Check your connection and try again.")
            setStatus('error')
        }
    }

    const submitting = status === 'submitting'

    return (
        <section id='contact' aria-labelledby='contact-title' className='relative bg-cream py-24 sm:py-32'>
            <Scallop className='text-cream' />

            <div className='mx-auto grid max-w-6xl gap-8 px-5 sm:px-8 lg:grid-cols-[0.9fr_1.1fr] lg:gap-10'>
                <div className='reveal-left relative flex flex-col rounded-[2.5rem] border-2 border-plum bg-lavender p-8 shadow-[0_6px_0_0_var(--color-plum)] sm:p-10'>
                    <IconArt
                        icon={Send}
                        shape='cloud'
                        colorClassName='text-cream'
                        accentClassName='text-butter'
                        className='size-32 sm:size-36'
                    />

                    <h2 id='contact-title' className='mt-8 font-heading text-[clamp(2.75rem,5.5vw,4.5rem)] leading-[0.92] text-plum'>
                        Got a project? Let's make some noise.
                    </h2>

                    <p className='mt-5 text-lg leading-relaxed text-plum/80'>
                        Tell us what you're building and we'll reply within one business day with next steps and a ballpark quote.
                    </p>

                    <ul className='mt-8 space-y-3'>
                        {PROMISES.map((promise) => (
                            <li key={promise} className='flex items-center gap-3 font-medium text-plum'>
                                <span className='grid size-6 shrink-0 place-items-center rounded-full bg-plum text-cream'>
                                    <Check className='size-3.5' strokeWidth={3} aria-hidden='true' />
                                </span>
                                {promise}
                            </li>
                        ))}
                    </ul>

                    <p className='mt-auto pt-10 text-plum/80'>
                        Prefer email? Write to{' '}
                        <a href={`mailto:${contactDetails.email}`} className='font-semibold text-plum underline decoration-2 underline-offset-4 hover:decoration-coral'>
                            {contactDetails.email}
                        </a>
                    </p>
                </div>

                <div className='reveal-right rounded-[2.5rem] border-2 border-plum bg-card p-7 shadow-[0_6px_0_0_var(--color-plum)] [--reveal-delay:150ms] sm:p-10'>
                    {status === 'success'
                        ? (
                            <div
                                ref={successRef}
                                tabIndex={-1}
                                role='status'
                                className='relative flex h-full flex-col items-center justify-center py-10 text-center outline-none animate-in fade-in zoom-in-50 duration-500'
                            >
                                <Confetti />

                                <IconArt
                                    icon={PartyPopper}
                                    shape='burst'
                                    colorClassName='text-butter'
                                    accentClassName='text-bubblegum'
                                    className='size-40'
                                />

                                <h3 className='mt-8 font-heading text-[clamp(2.25rem,4vw,3rem)] leading-none text-plum'>
                                    Huzzah! Message received.
                                </h3>

                                <p className='mt-4 max-w-sm text-lg leading-relaxed text-plum/80'>
                                    Thanks for reaching out. We'll get back to you within one business day, usually much sooner.
                                </p>

                                <Button variant='secondary' className='mt-8' onClick={() => setStatus('idle')}>
                                    Send another message
                                </Button>
                            </div>
                        )
                        : (
                            <form noValidate onSubmit={onSubmit} className='relative' aria-describedby='contact-form-note'>
                                <h3 className='font-heading text-3xl text-plum'>
                                    Tell us about it
                                </h3>

                                <p id='contact-form-note' className='mt-1 text-sm text-plum/70'>
                                    Fields marked with * are required.
                                </p>

                                <div className='mt-7 grid gap-5 sm:grid-cols-2'>
                                    <FormField id='contact-name' label='Your name *' error={errors.name}>
                                        <input
                                            id='contact-name'
                                            name='name'
                                            type='text'
                                            autoComplete='name'
                                            required
                                            maxLength={CONTACT_LIMITS.name}
                                            value={values.name}
                                            onChange={update('name')}
                                            aria-invalid={Boolean(errors.name)}
                                            aria-describedby={describedBy('name')}
                                            placeholder="Jamie Rivera"
                                            className={inputClasses}
                                        />
                                    </FormField>

                                    <FormField id='contact-email' label='Email *' error={errors.email}>
                                        <input
                                            id='contact-email'
                                            name='email'
                                            type='email'
                                            inputMode='email'
                                            autoComplete='email'
                                            required
                                            maxLength={CONTACT_LIMITS.email}
                                            value={values.email}
                                            onChange={update('email')}
                                            aria-invalid={Boolean(errors.email)}
                                            aria-describedby={describedBy('email')}
                                            placeholder="jamie@yourbrand.com"
                                            className={inputClasses}
                                        />
                                    </FormField>

                                    <FormField id='contact-phone' label='Phone' error={errors.phone}>
                                        <input
                                            id='contact-phone'
                                            name='phone'
                                            type='tel'
                                            inputMode='tel'
                                            autoComplete='tel'
                                            maxLength={CONTACT_LIMITS.phone}
                                            placeholder="(503) 555-0142"
                                            value={values.phone}
                                            onChange={update('phone')}
                                            aria-invalid={Boolean(errors.phone)}
                                            aria-describedby={describedBy('phone')}
                                            className={inputClasses}
                                        />
                                    </FormField>

                                    <FormField id='contact-topic' label='What can we help with?' error={errors.topic}>
                                        <select
                                            id='contact-topic'
                                            name='topic'
                                            value={values.topic}
                                            onChange={update('topic')}
                                            aria-invalid={Boolean(errors.topic)}
                                            aria-describedby={describedBy('topic')}
                                            className={cn(inputClasses, 'appearance-none pr-11', !values.topic && 'text-plum/50')}
                                        >
                                            <option value=''>Pick a topic</option>

                                            {CONTACT_TOPICS.map((topic) => (
                                                <option key={topic} value={topic} className='text-plum'>
                                                    {topic}
                                                </option>
                                            ))}
                                        </select>

                                        <ChevronDown className='pointer-events-none absolute top-1/2 right-4 size-5 -translate-y-1/2 text-plum' aria-hidden='true' />
                                    </FormField>

                                    <FormField id='contact-message' label='Your message *' error={errors.message} className='sm:col-span-2'>
                                        <textarea
                                            id='contact-message'
                                            name='message'
                                            required
                                            rows={5}
                                            maxLength={CONTACT_LIMITS.message}
                                            value={values.message}
                                            onChange={update('message')}
                                            aria-invalid={Boolean(errors.message)}
                                            aria-describedby={describedBy('message')}
                                            placeholder="What are you working on, and when would you like to launch?"
                                            className={cn(inputClasses, 'min-h-36 resize-y')}
                                        />
                                    </FormField>
                                </div>

                                <div aria-hidden='true' className='absolute -left-[9999px] h-px w-px overflow-hidden'>
                                    <label htmlFor='contact-website'>Leave this field empty</label>
                                    <input
                                        id='contact-website'
                                        name='website'
                                        type='text'
                                        tabIndex={-1}
                                        autoComplete='off'
                                        value={honeypot}
                                        onChange={(event) => setHoneypot(event.target.value)}
                                    />
                                </div>

                                {status === 'error' && serverMessage && (
                                    <p role='alert' className='mt-6 flex items-start gap-2 rounded-2xl border-2 border-red-600/30 bg-red-50 px-4 py-3 text-sm font-medium text-red-800'>
                                        <CircleAlert className='mt-0.5 size-4 shrink-0' aria-hidden='true' />
                                        {serverMessage}
                                    </p>
                                )}

                                <div className='mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
                                    <p className='max-w-xs text-xs leading-relaxed text-plum/60'>
                                        We only use your details to reply to you. No newsletters, no sharing.
                                    </p>

                                    <Button type='submit' size='lg' disabled={submitting} aria-disabled={submitting}>
                                        {submitting
                                            ? <LoaderCircle className='size-5 animate-spin' aria-hidden='true' />
                                            : <Send className='size-5' aria-hidden='true' />}
                                        {submitting ? 'Sending...' : 'Send message'}
                                    </Button>
                                </div>
                            </form>
                        )}
                </div>
            </div>
        </section>
    )
}
